import React, { Suspense, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  ContentCards,
  ImageCard,
  Hyperlink,
  SpinningLoader,
  Toast,
  Button,
  InlineAlert,
} from 'br2s-ui-componentlibrary_r2_02';
import BuyInformationImg from '@/assets/overview-buy-information.png';
import DownloadImg from '@/assets/overview-buy-information-download.png';
import VCCImg from '@/assets/overview-buy-information-vcc.png';
import TrustBarImg from '@/assets/overview-buy-information-trust-bar.png';
import PDFProductImg from '@/assets/overview-buy-information-pdf-product.png';
import { ANNOUNCEMENT, INFORMATION_PRODUCTS } from '../overview-constants';
import {
  HTTPErrorAlertContainer,
  reportDtCustomError,
  useHTTPError,
} from 'br2s-ui-common-utils-02';
import { ISHOP_LINKS } from '@/utilities/constants';
import { PAGE_PATH } from '@/utilities/constants/content';
import { useStore } from '@/redux/store';
import classNames from 'classnames';
import './BuyInformation.scss';

const PageLoaderModule = React.lazy(() => import('content/PageLoaderModule'));
const IpsFederatedSearchModule = React.lazy(
  () => import('ips/IpsFederatedSearchModule'),
);

const BuyInformation = () => {
  const navigate = useNavigate();
  const [hasAnnouncement, setHasAnnouncement] = useState(false);
  const [toast, setToast] = useState({
    isDisplayed: false,
  });
  const [fedSearchError, setFedSearchError] = useState({
    isDisplayed: false,
    headline: '',
  });
  const [searchToast, setSeachToast] = useState({
    isDisplayed: false,
    description: '',
  });
  const { handleHTTPError } = useHTTPError();
  const { toggleLoading } = useStore();

  const hideToast = () => setToast({ isDisplayed: false });
  // istanbul ignore next
  const hidesearchToast = () =>
    setSeachToast({ isDisplayed: false, description: '' });

  const openLink = (path: string, newTab = false) => {
    if (newTab) {
      window.open(path, '_blank');
      return false;
    }

    navigate(`/${path}`);
  };

  // istanbul ignore next
  const handleFederatedSearchError = (error: any) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (error.response?.status === 429) {
      setFedSearchError({
        isDisplayed: true,
        headline: 'We are experiencing high traffic. Please try again later.',
      });
    } else if (error.response?.status === 400) {
      setSeachToast({
        isDisplayed: true,
        description: error.response?.data?.error?.errorDesc,
      });
    } else {
      handleHTTPError(error, { errorType: 'inline' });
      console.error(error);
      reportDtCustomError('GenericHandledError', JSON.stringify(error));
    }
  };

  return (
    <div className="buy-information-page">
      <div className="header-container">
        <HTTPErrorAlertContainer />
        {fedSearchError.isDisplayed && (
          <div className="container">
            <InlineAlert
              id="fedsearch-err"
              type="error"
              subHeadline={fedSearchError.headline}
            >
              <div style={{ paddingTop: '12px' }}>
                <Button
                  id="fedsearch-err-btn"
                  label="Dismiss"
                  type="quiet"
                  onClick={() =>
                    setFedSearchError((prev) => ({
                      ...prev,
                      isDisplayed: false,
                    }))
                  }
                />
              </div>
            </InlineAlert>
          </div>
        )}
        <div className="container">
          <div className="page-header-container">
            <div>
              <Typography
                type="headline-2"
                weight="bold"
                label="Unlock the power of business data"
              />
              <Typography
                type="headline-5"
                label="Access our business information products and services for insights to make informed decisions and drive growth."
              />
            </div>
            <div className="overview-icon">
              <img src={BuyInformationImg} alt="overview-buy-information" />
            </div>
          </div>

          <div className="overview-glass-container">
            <div className="content federated-search-container">
              <Suspense fallback={<SpinningLoader />}>
                <IpsFederatedSearchModule
                  handleSetToastMessage={(toast) =>
                    setToast({ ...toast, isDisplayed: true })
                  }
                  showGlobalSpinningLoader={(boolean) => toggleLoading(boolean)}
                  handleOnSearchError={handleFederatedSearchError}
                />
              </Suspense>
            </div>
            <div>
              <div className="card-section-container">
                <div>
                  <Typography
                    type="headline-3"
                    weight="bold"
                    label="Information products"
                  />
                  <Typography
                    type="subheadline"
                    label="Explore the different business products we offer below."
                  />
                </div>
                <div className="carousel-content">
                  {INFORMATION_PRODUCTS.map((data, index) => (
                    <ContentCards
                      key={`cmp-content-card-${index + 1}`}
                      id={`cmp-content-card-${index + 1}`}
                      title={data.title}
                      description={data.description}
                      footerBtn={[
                        {
                          label: 'Learn more',
                          size: 'medium',
                          type: 'quiet',
                          onClick: () => openLink(data.path),
                        },
                      ]}
                    />
                  ))}
                </div>
              </div>
              <div
                className={classNames('announcement-container', {
                  'd-none': !hasAnnouncement,
                })}
              >
                <div className="row">
                  <div className="col-12 col-lg-5 col-xl-4 p-0">
                    <div className="announcement-header-container">
                      <Typography
                        type="headline-4"
                        label={ANNOUNCEMENT.TITLE}
                        weight="bold"
                      />
                      <Typography label={ANNOUNCEMENT.DESCRIPTION} />
                    </div>
                  </div>
                  <div className="col-12 col-lg-7 col-xl-8 carousel-wrapper">
                    <div className="carousel-container">
                      <Suspense fallback={<></>}>
                        <PageLoaderModule
                          pagePath={PAGE_PATH.ANNOUNCEMENT.BUY_INFORMATION}
                          hasAreaComponents={(count: number) =>
                            setHasAnnouncement(!!count)
                          }
                        />
                      </Suspense>
                    </div>
                  </div>
                </div>
              </div>
              <div className="vcc-container">
                <ImageCard
                  id="cmp-image-card-vcc"
                  title="Variable Capital Companies (VCC) business information"
                  description="Business information on VCCs can be purchased at the VCC portal."
                  layout="horizontal"
                  imgSrc={VCCImg}
                  imgAlt="vcc"
                  actionButtons={[
                    {
                      id: 'action',
                      type: 'secondary',
                      label: 'Go to VCC portal',
                      onClick: () => openLink(ISHOP_LINKS.VCC_PORTAL, true),
                    },
                  ]}
                />
                <div className="hyperlink-content">
                  All repository and public access of documents and information
                  relating to business entities and public accountants is stated
                  under{' '}
                  <Hyperlink
                    id="cmp-hyperlink"
                    label="ACRA's Data Policy Statement."
                    href={ISHOP_LINKS.ACRA_DATA_POLICY}
                    fontStyle="label"
                    isOpenNewTab
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="footer-container">
        <div className="container">
          <div className="download-container">
            <div>
              <Typography type="headline-3" weight="bold" label="Download" />
            </div>
            <ImageCard
              id="cmp-image-card-download"
              title="Product download"
              description="Retrieve and download your purchased business information products easily by entering the product download code."
              layout="horizontal"
              imgSrc={DownloadImg}
              imgAlt="download"
              actionButtons={[
                {
                  id: 'action',
                  type: 'secondary',
                  label: 'Retrieve products',
                  onClick: () => openLink(ISHOP_LINKS.PRODUCT_DOWNLOAD),
                },
              ]}
            />
          </div>

          <div className="authenticate-container">
            <div>
              <Typography
                type="headline-3"
                weight="bold"
                label="Authenticate"
              />
            </div>
            <div className="image-cards-content">
              <ImageCard
                id="cmp-image-card-trust-bar"
                title="trustBar authentication"
                description="Quick and easy way to authenticate if the Business Profiles and Business Certificates are issued by ACRA."
                layout="vertical"
                imgSrc={TrustBarImg}
                imgAlt="trust-bar"
                actionButtons={[
                  {
                    id: 'action',
                    type: 'secondary',
                    label: 'Authenticate',
                    onClick: () =>
                      openLink(ISHOP_LINKS.TRUSTBAR_AUTHENTICATE, true),
                  },
                ]}
              />
              <ImageCard
                id="cmp-image-card-pdf"
                title="PDF product authentication"
                description="Check the validity and authenticity of ACRA-certified documents easily."
                layout="vertical"
                imgSrc={PDFProductImg}
                imgAlt="pdf-product"
                actionButtons={[
                  {
                    id: 'action',
                    type: 'secondary',
                    label: 'Authenticate',
                    onClick: () => openLink(ISHOP_LINKS.PDF_PROD_AUTHENTICATE),
                  },
                ]}
              />
            </div>
          </div>
        </div>
      </div>

      <Toast
        id="federated-search-toast"
        {...toast}
        onDismiss={hideToast}
        onDismissClick={hideToast}
      />

      <Toast
        id="error-search-toast"
        {...searchToast}
        headline=""
        type="error"
        onDismiss={hidesearchToast}
        onDismissClick={hidesearchToast}
      />
    </div>
  );
};

export default BuyInformation;
